import React from "react";
import { ChevronUp, ChevronDown, CheckCircle } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import { SubscriptionApprovalRow } from "./SubscriptionApprovalRow";
import { Subscription } from "@/lib/subscriptionsData";

interface SubscriptionApprovalTableProps {
  isDarkMode: boolean;
  subscriptions: Subscription[];
  sortField:
    | "patientName"
    | "medication"
    | "prescribedBy"
    | "nextSupplyDate"
    | "status";
  sortDirection: "asc" | "desc";
  onSort: (
    field:
      | "patientName"
      | "medication"
      | "prescribedBy"
      | "nextSupplyDate"
      | "status"
  ) => void;
  selectAll: boolean;
  onSelectAll: (checked: boolean) => void;
  selectedSubscriptions: Set<string>;
  onSelectSubscription: (subscriptionId: string, checked: boolean) => void;
  onProcessSubscription: (subscription: Subscription) => void;
}

export const SubscriptionApprovalTable = React.memo(
  function SubscriptionApprovalTable({
    isDarkMode,
    subscriptions,
    sortField,
    sortDirection,
    onSort,
    selectAll,
    onSelectAll,
    selectedSubscriptions,
    onSelectSubscription,
    onProcessSubscription,
  }: SubscriptionApprovalTableProps) {
    return (
      <div
        className={`rounded-lg border ${
          isDarkMode
            ? "border-gray-700 bg-gray-800"
            : "border-gray-200 bg-white"
        }`}
      >
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className={`${isDarkMode ? "bg-gray-700" : "bg-gray-50"}`}>
              <tr>
                <th
                  className={`px-3 py-3 text-left ${
                    isDarkMode ? "text-gray-300" : "text-gray-500"
                  }`}
                >
                  <Checkbox
                    checked={selectAll}
                    onCheckedChange={(checked) => onSelectAll(!!checked)}
                    aria-label="Select all subscriptions"
                  />
                </th>
                <th
                  className={`px-6 py-3 text-left text-xs font-medium ${
                    isDarkMode ? "text-gray-300" : "text-gray-500"
                  } uppercase tracking-wider`}
                >
                  <button
                    onClick={() => onSort("patientName")}
                    className="flex items-center gap-1 hover:text-gray-900 transition-colors"
                  >
                    Patient
                    {sortField === "patientName" &&
                      (sortDirection === "asc" ? (
                        <ChevronUp className="w-3 h-3" />
                      ) : (
                        <ChevronDown className="w-3 h-3" />
                      ))}
                  </button>
                </th>
                <th
                  className={`px-6 py-3 text-left text-xs font-medium ${
                    isDarkMode ? "text-gray-300" : "text-gray-500"
                  } uppercase tracking-wider`}
                >
                  <button
                    onClick={() => onSort("medication")}
                    className="flex items-center gap-1 hover:text-gray-900 transition-colors"
                  >
                    Medication
                    {sortField === "medication" &&
                      (sortDirection === "asc" ? (
                        <ChevronUp className="w-3 h-3" />
                      ) : (
                        <ChevronDown className="w-3 h-3" />
                      ))}
                  </button>
                </th>
                <th
                  className={`px-6 py-3 text-left text-xs font-medium ${
                    isDarkMode ? "text-gray-300" : "text-gray-500"
                  } uppercase tracking-wider`}
                >
                  <button
                    onClick={() => onSort("prescribedBy")}
                    className="flex items-center gap-1 hover:text-gray-900 transition-colors"
                  >
                    Prescribed By
                    {sortField === "prescribedBy" &&
                      (sortDirection === "asc" ? (
                        <ChevronUp className="w-3 h-3" />
                      ) : (
                        <ChevronDown className="w-3 h-3" />
                      ))}
                  </button>
                </th>
                <th
                  className={`px-6 py-3 text-left text-xs font-medium ${
                    isDarkMode ? "text-gray-300" : "text-gray-500"
                  } uppercase tracking-wider`}
                >
                  Issue Progress
                </th>
                <th
                  className={`px-6 py-3 text-left text-xs font-medium ${
                    isDarkMode ? "text-gray-300" : "text-gray-500"
                  } uppercase tracking-wider`}
                >
                  Last Supply
                </th>
                <th
                  className={`px-6 py-3 text-left text-xs font-medium ${
                    isDarkMode ? "text-gray-300" : "text-gray-500"
                  } uppercase tracking-wider`}
                >
                  <button
                    onClick={() => onSort("nextSupplyDate")}
                    className="flex items-center gap-1 hover:text-gray-900 transition-colors"
                  >
                    Next Supply
                    {sortField === "nextSupplyDate" &&
                      (sortDirection === "asc" ? (
                        <ChevronUp className="w-3 h-3" />
                      ) : (
                        <ChevronDown className="w-3 h-3" />
                      ))}
                  </button>
                </th>
                <th
                  className={`px-6 py-3 text-left text-xs font-medium ${
                    isDarkMode ? "text-gray-300" : "text-gray-500"
                  } uppercase tracking-wider`}
                >
                  Value
                </th>
                <th
                  className={`px-6 py-3 text-left text-xs font-medium ${
                    isDarkMode ? "text-gray-300" : "text-gray-500"
                  } uppercase tracking-wider`}
                >
                  <button
                    onClick={() => onSort("status")}
                    className="flex items-center gap-1 hover:text-gray-900 transition-colors"
                  >
                    Status
                    {sortField === "status" &&
                      (sortDirection === "asc" ? (
                        <ChevronUp className="w-3 h-3" />
                      ) : (
                        <ChevronDown className="w-3 h-3" />
                      ))}
                  </button>
                </th>
                <th
                  className={`px-6 py-3 text-right text-xs font-medium ${
                    isDarkMode ? "text-gray-300" : "text-gray-500"
                  } uppercase tracking-wider`}
                >
                  Actions
                </th>
              </tr>
            </thead>
            <tbody
              className={`${isDarkMode ? "bg-gray-800" : "bg-white"} divide-y ${
                isDarkMode ? "divide-gray-700" : "divide-gray-200"
              }`}
            >
              {subscriptions.map((subscription) => (
                <SubscriptionApprovalRow
                  key={subscription.id}
                  subscription={subscription}
                  isDarkMode={isDarkMode}
                  isSelected={selectedSubscriptions.has(subscription.id)}
                  onSelect={(checked) =>
                    onSelectSubscription(subscription.id, checked)
                  }
                  onProcess={() => onProcessSubscription(subscription)}
                />
              ))}
            </tbody>
          </table>
        </div>

        {subscriptions.length === 0 && (
          <div className="text-center py-12">
            <CheckCircle
              className={`w-12 h-12 mx-auto mb-4 ${
                isDarkMode ? "text-gray-500" : "text-gray-400"
              }`}
            />
            <h3
              className={`text-lg font-medium mb-2 ${
                isDarkMode ? "text-white" : "text-gray-900"
              }`}
            >
              No subscriptions found
            </h3>
            <p className={`${isDarkMode ? "text-gray-300" : "text-gray-600"}`}>
              No subscription prescriptions match the current filters.
            </p>
          </div>
        )}
      </div>
    );
  }
);
